import React, { useEffect, useState } from 'react';
import { Badge, Button, Col, Form, Modal, Row, Select, Space } from 'antd';
import { ClearOutlined, FilterOutlined } from '@ant-design/icons';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import useFetch from '../../../../../hooks/useFetch';
import { FetchApiInterface } from '../../../../../interfaces/FetchApi.interface';

const { Option } = Select;

interface FiltersTraductorProps {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  filterApply: any;
  onFinish: (values: any) => void;
  cleanFilters: () => void;
}

const FiltersTraductor: React.FC<FiltersTraductorProps> = ({
  filterApply,
  onFinish,
  cleanFilters,
  openModal,
  setOpenModal,
}) => {
  const [form] = Form.useForm();
  const { fetchApi } = useFetch();
  const { t } = useTranslation();
  const [statuses,   setStatuses]   = useState<Array<any>>([]);
  const [countries,  setCountries]  = useState<Array<any>>([]);
  const [requesters, setRequesters] = useState<Array<any>>([]);

  useEffect(() => {
    fetchApi({ url: '/translations/status/options', method: 'GET' } as FetchApiInterface)
      .then(({ data }: AxiosResponse<any>) => {
        setStatuses(Array.isArray(data) ? data : data?.data || []);
      }).catch(() => {});

    fetchApi({ url: '/users/get_communities', method: 'POST', data: {} } as FetchApiInterface)
      .then(({ data }: AxiosResponse<any>) => {
        setCountries(Array.isArray(data) ? data : data?.data || []);
      }).catch(() => {});

    fetchApi({ url: '/users/get_requester_by_user_id', method: 'GET' } as FetchApiInterface)
      .then(({ data }: AxiosResponse<any>) => {
        setRequesters(Array.isArray(data) ? data : data?.data || []);
      }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClear = () => {
    form.resetFields();
    cleanFilters();
  };

  const hasFilters = Object.values(filterApply).some(
    v => Array.isArray(v) ? v.length > 0 : v != null
  );

  return (
    <Col>
      <Space>
        <Badge dot={hasFilters}>
          <Button icon={<FilterOutlined />} onClick={() => setOpenModal(true)}>
            {t('filters')}
          </Button>
        </Badge>
        {hasFilters && (
          <Button type="primary" danger ghost icon={<ClearOutlined />} onClick={handleClear}>
            {t('clearFilters')}
          </Button>
        )}
      </Space>

      <Modal
        centered
        width={700}
        title={t('filters')}
        open={openModal}
        onCancel={() => setOpenModal(false)}
        footer={
          <Row justify="space-between">
            <Col>
              <Button shape="round" type="primary" danger ghost onClick={handleClear}>
                {t('clearFilters')}
              </Button>
            </Col>
            <Col>
              <Space>
                <Button shape="round" type="text" onClick={() => setOpenModal(false)}>
                  {t('cancel')}
                </Button>
                <Button shape="round" type="primary" onClick={() => form.submit()}>
                  {t('applyFilters')}
                </Button>
              </Space>
            </Col>
          </Row>
        }
      >
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Row gutter={[16, 0]}>

            <Col span={24} md={12}>
              <Form.Item name="statuses" label={t('status')}>
                <Select mode="multiple" allowClear showSearch placeholder={t('selectOption')} listHeight={160}
                  optionFilterProp="children">
                  {statuses.map((s: any) => (
                    <Option key={s.value} value={s.value}>{t(s.description)}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24} md={12}>
              <Form.Item name="countryIds" label={t('country')}>
                <Select mode="multiple" allowClear showSearch placeholder={t('selectOption')} listHeight={160}
                  optionFilterProp="children">
                  {countries.map((c: any) => (
                    <Option key={c.dpaId ?? c.id} value={c.dpaId ?? c.id}>
                      {c.dpaName ?? c.name ?? c.dpaId}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24} md={12}>
              <Form.Item name="requesterIds" label={t('requestedBy')}>
                <Select mode="multiple" allowClear showSearch placeholder={t('selectOption')} listHeight={160}
                  optionFilterProp="children">
                  {requesters.map((r: any) => {
                    const id = r.useId ?? r.userId ?? r.id;
                    return (
                      <Option key={id} value={id}>
                        {r.fullName}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

          </Row>
        </Form>
      </Modal>
    </Col>
  );
};

export default FiltersTraductor;
